import {
    Box,
    Typography,
    TextField,
    IconButton,
    styled,
    CircularProgress,
    InputAdornment,
    Autocomplete,
    FormControl,
    Select,
    InputLabel,
    MenuItem,
    ListSubheader,
    Tooltip,
    Skeleton,
    Chip,
} from '@mui/material';
import { Remove, OpenInNew, SwapHoriz } from '@mui/icons-material';
import type { MpvCultureMapping } from 'src/services/MsxService';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useRecoilValue } from 'recoil';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useQueryProductVersion } from 'src/hooks/useQueryProductVersion';
import { useQueryProducts } from 'src/components/ProductSearchPage/hooks/useQueryProducts';
import { useAvailableLocales } from 'src/hooks/useAvailableLocales';
import { config } from 'src/lib/config';
import { DEFAULT_CULTURE } from 'src/constants';
import { isValidPrd } from 'src/utils/productKeyValidation';
import { useQueryMpvLookup } from 'src/hooks/useQueryMpvLookup';

export type ProductOptionsMap = Record<string, string>;
type Row = { id: string; key: string; value: string };

export type TargetProductOptionsProps = {
    productKey: string;
    version?: number;
    culture: string;
    onChangeProductKey: (prd: string) => void;
    onChangeVersion: (version?: number) => void;
    onChangeOptions?: (opts: ProductOptionsMap) => void;
    onChangeCulture: (culture: string) => void;
    initialOptions?: ProductOptionsMap;
};

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiInputBase-root.Mui-focused': {
        backgroundColor: theme.palette.custom.lightBlue,
    },
}));

const Card = styled(Box)(({ theme }) => ({
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
}));

const RowGrid = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr auto',
    gap: theme.spacing(2),
    alignItems: 'start',
    marginBottom: theme.spacing(2),
}));

const StyledInputAdornment = styled(InputAdornment)(({ theme }) => ({
    height: '100%',
    position: 'absolute',
    right: theme.spacing(6),
    top: `calc(50% - ${theme.spacing(3)})`,
}));

const StyledHeaderGrid = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '4fr 2fr 3fr',
    gap: theme.spacing(2),
}));

const uid = (): string => uuidv4();

const NA_CULTURE_PRIORITY = ['en-us', 'en-ca', 'fr-ca', 'es-us'];

const orderNaFirst = (cultures: string[]): string[] => {
    const naHits = NA_CULTURE_PRIORITY.filter((c) => cultures.includes(c));
    const rest = cultures.filter((c) => !NA_CULTURE_PRIORITY.includes(c));

    return [...naHits, ...rest];
};

const REGION_PRIMARY_CULTURE: Record<string, string> = {
    'North America': 'en-us',
    Europe: 'en-gb',
};

const regionOfCulture = (culture: string, groupedLocales: Record<string, string[]>): string | undefined => Object
    .keys(groupedLocales)
    .find((region) => groupedLocales[region].includes(culture));

const collapseAlternatesByRegion = (
    alternates: MpvCultureMapping[],
    pickedCulture: string,
    groupedLocales: Record<string, string[]>,
): MpvCultureMapping[] => {
    const pickedRegion = regionOfCulture(pickedCulture, groupedLocales);
    const result: MpvCultureMapping[] = [];

    Object.entries(groupedLocales).forEach(([region, locales]) => {
        if (region === pickedRegion) return;
        const inRegion = alternates.filter((alt) => locales.includes(alt.culture));

        if (inRegion.length === 0) return;
        const primary = REGION_PRIMARY_CULTURE[region];

        const byProductKey = new Map<string, MpvCultureMapping[]>();

        inRegion.forEach((alt) => {
            const group = byProductKey.get(alt.productKey) ?? [];

            group.push(alt);
            byProductKey.set(alt.productKey, group);
        });

        const reps = [...byProductKey.values()].map((group) => group.find((alt) => alt.culture === primary)
            ?? [...group].sort((a, b) => a.culture.localeCompare(b.culture))[0]);

        reps.sort((a, b) => a.culture.localeCompare(b.culture));
        result.push(...reps);
    });
    return result;
};

const rowsToOptions = (rows: Row[]): ProductOptionsMap => {
    const options: ProductOptionsMap = {};

    rows.forEach((row) => {
        const k = row.key.trim();
        const v = row.value.trim();

        if (k && v) options[k] = v;
    });
    return options;
};

const isComplete = (r: Row): boolean => r.key.trim().length > 0 && r.value.trim().length > 0;
const isEmptyRow = (r: Row): boolean => r.key.trim() === '' && r.value.trim() === '';

const addEmptyRowIfNeeded = (list: Row[], maxRows: number): Row[] => {
    if (!list.length) return [{ id: uid(), key: '', value: '' }];
    const last = list[list.length - 1];
    const hasEmptyLast = isEmptyRow(last);
    const allComplete = list.every(isComplete);
    const hasIncompleteBeforeLast = list.slice(0, -1).some((r) => !isComplete(r));

    if (hasEmptyLast && hasIncompleteBeforeLast) return list.slice(0, -1);
    if (!hasEmptyLast && allComplete && list.length < maxRows) {
        return [...list, { id: uid(), key: '', value: '' }];
    }
    return list;
};

export const TargetProductOptions = ({
    productKey,
    version,
    culture,
    onChangeProductKey,
    onChangeVersion,
    onChangeOptions,
    onChangeCulture,
    initialOptions,
}: TargetProductOptionsProps): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);

    const [rows, setRows] = useState<Row[]>([]);
    const bottomRef = useRef<HTMLDivElement | null>(null);

    const [lastKey, setLastKey] = useState<string>('');
    const [lastVersion, setLastVersion] = useState<number | undefined>(undefined);

    const prdCandidate: string = (productKey ?? '').trim();
    const prdIsValid: boolean = isValidPrd(prdCandidate);
    const looksLikeMpvId: boolean = prdCandidate.length >= 3
        && !prdIsValid;

    const [appliedInitial, setAppliedInitial] = useState(false);

    const { groupedLocales, locales: allLocales } = config as unknown as {
        groupedLocales: Record<string, string[]>;
        locales: string[];
    };

    const cultureIsAuto = !culture;
    const lookupCultures = cultureIsAuto ? orderNaFirst(allLocales) : [culture];
    const lookupCulturesKey = lookupCultures.join('|');

    const [alternates, setAlternates] = useState<MpvCultureMapping[]>([]);
    const [alternatesAnchor, setAlternatesAnchor] = useState<{ productKey: string; culture: string } | null>(null);

    const mpvLookupQuery = useQueryMpvLookup(prdCandidate, lookupCultures, { enabled: looksLikeMpvId });
    const isResolvingMpv = mpvLookupQuery.isFetching;
    const resolved = mpvLookupQuery.data ?? null;
    const mpvResolveError = ((): string | null => {
        if (mpvLookupQuery.isFetching) return null;
        if (mpvLookupQuery.error) return mpvLookupQuery.error.message || 'Failed to resolve MPV ID.';
        if (mpvLookupQuery.isFetched && !mpvLookupQuery.data) {
            return cultureIsAuto
                ? `No product key found for MPV "${prdCandidate}" in any configured culture.`
                : `No product key found for MPV "${prdCandidate}" in ${culture}.`;
        }
        return null;
    })();

    const resolvedForMpv = mpvLookupQuery.resolvedFor.mpvId;
    const resolvedForCulturesKey = mpvLookupQuery.resolvedFor.cultures.join('|');

    useEffect(() => {
        // Guard on looksLikeMpvId: MSX maps a resolved PRD back to its MPV, so without it the
        // cached lookup would flip the field back to the MPV and loop.
        if (!resolved) return;
        if (!looksLikeMpvId) return;
        if (resolvedForMpv !== prdCandidate) return;
        if (resolvedForCulturesKey !== lookupCulturesKey) return;
        onChangeProductKey(resolved.productKey);
        if (cultureIsAuto && resolved.culture && resolved.culture !== culture) {
            onChangeCulture(resolved.culture);
        }
        setAlternates(resolved.alternates);
        setAlternatesAnchor({ productKey: resolved.productKey, culture: resolved.culture });
    }, [
        resolved,
        looksLikeMpvId,
        resolvedForMpv,
        resolvedForCulturesKey,
        prdCandidate,
        lookupCulturesKey,
        cultureIsAuto,
        culture,
        onChangeProductKey,
        onChangeCulture,
    ]);

    useEffect(() => {
        if (!alternatesAnchor) return;
        if (alternatesAnchor.productKey !== prdCandidate) {
            setAlternates([]);
            setAlternatesAnchor(null);
        }
    }, [alternatesAnchor, prdCandidate]);

    const applyAlternate = (alt: MpvCultureMapping): void => {
        if (!alternatesAnchor) return;
        const previous = alternatesAnchor;
        const remaining = alternates.filter(
            (a) => a.productKey !== alt.productKey || a.culture !== alt.culture,
        );

        onChangeProductKey(alt.productKey);
        onChangeCulture(alt.culture);
        setAlternates([previous, ...remaining]);
        setAlternatesAnchor({ productKey: alt.productKey, culture: alt.culture });
    };

    const collapsedAlternates = collapseAlternatesByRegion(
        alternates,
        alternatesAnchor?.culture ?? culture,
        groupedLocales,
    );

    const { data: productResp, isLoading } = useQueryProductVersion(
        accessToken,
        { productKey: prdIsValid ? prdCandidate : '', version },
        { enabled: prdIsValid, suspense: false },
    );

    const { data: productsData, isLoading: isLoadingProductName } = useQueryProducts(
        accessToken,
        { productKey: prdIsValid ? prdCandidate : '' },
        { enabled: prdIsValid, suspense: false },
    );

    const activeProduct = productResp?.result?.find((v) => v.isCurrent);
    const product = version
        ? productResp?.result?.find((v) => v.version === version)
        : activeProduct ?? productResp?.result?.[0];

    const productOptions = product?.options ?? [];
    const activeVersion = product?.version;

    const { availableLocales } = useAvailableLocales(
        prdIsValid ? prdCandidate : null,
        activeVersion ?? null,
    );

    const filteredGroupedLocales = Object.entries(groupedLocales).reduce((acc, [region, locales]) => {
        const filtered = availableLocales.length > 0
            ? locales.filter((locale) => availableLocales.includes(locale))
            : locales;

        if (filtered.length > 0) acc[region] = filtered;
        return acc;
    }, {} as Record<string, string[]>);

    const optionNames: string[] = [];
    const valuesByName: Record<string, string[]> = {};

    useEffect(() => {
        if (productKey.trim() === '') {
            setRows([{ id: uid(), key: '', value: '' }]);
        }
    }, [productKey]);

    useEffect(() => {
        if (!culture) return;
        if (availableLocales.length > 0 && !availableLocales.includes(culture)) {
            if (availableLocales.includes(DEFAULT_CULTURE)) {
                onChangeCulture(DEFAULT_CULTURE);
            } else {
                const sortedLocales = [...availableLocales].sort();

                onChangeCulture(sortedLocales[0]);
            }
        }
    }, [availableLocales, culture, onChangeCulture]);

    useEffect(() => {
        if (appliedInitial) return;
        const entries = Object.entries(initialOptions ?? {});

        if (!entries.length) return;
        setRows(entries.map(([key, value]) => ({ id: uid(), key, value })));
        setAppliedInitial(true);
    }, [initialOptions, appliedInitial]);

    for (const opt of productOptions) {
        const option = opt as { name?: string; values?: unknown };
        const name = String(option.name ?? '');

        if (name) {
            optionNames.push(name);
            const rawVals = (option.values ?? []) as any[];
            const vals: string[] = Array.isArray(rawVals)
                ? rawVals.map((v: any) => (typeof v === 'string' ? v : String(v?.name ?? ''))).filter(Boolean)
                : [];

            valuesByName[name] = vals;
        }
    }
    optionNames.sort((a, b) => b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' }));
    for (const k of optionNames) {
        valuesByName[k] = [...(valuesByName[k] ?? [])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    }

    // When product+version resolves for the first time, seed a single empty row so the user
    // can pick names. Skip if the user has already typed rows or if initialOptions seeded them.
    if (
        prdIsValid
        && product
        && (lastKey !== prdCandidate || lastVersion !== product.version)
    ) {
        setLastKey(prdCandidate);
        setLastVersion(product.version);

        if (version === undefined && activeVersion != null) onChangeVersion(activeVersion);

        if (rows.every(isEmptyRow)) {
            setRows([{ id: uid(), key: '', value: '' }]);
            onChangeOptions?.({});
        } else {
            const next = addEmptyRowIfNeeded(rows, optionNames.length || rows.length + 1);

            if (next !== rows) setRows(next);
        }
    }

    const applyRowChanges = (next: Row[]): void => {
        const normalized = addEmptyRowIfNeeded(next, optionNames.length || next.length);
        const grew = normalized.length > rows.length;

        setRows(normalized);
        onChangeOptions?.(rowsToOptions(normalized));
        if (grew) {
            requestAnimationFrame(() => {
                bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            });
        }
    };

    const updateRowKey = (id: string, key: string): void => {
        const normalized = key.trim().toLowerCase();
        const me = rows.findIndex((r) => r.id === id);

        if (optionNames.length > 0 && rows.some((r, i) => i !== me && r.key.trim().toLowerCase() === normalized)) {
            return;
        }
        const vals = valuesByName[key] ?? [];
        const nextValue = vals.length > 0 ? vals[0] : '';

        applyRowChanges(rows.map((r) => (r.id === id ? { ...r, key, value: nextValue } : r)));
    };

    const updateRowValue = (id: string, value: string): void => {
        applyRowChanges(rows.map((r) => (r.id === id ? { ...r, value } : r)));
    };

    const removeRow = (id: string): void => {
        const next = rows.filter((r) => r.id !== id);

        applyRowChanges(next.length ? next : [{ id: uid(), key: '', value: '' }]);
    };

    const handleProductKeyChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const next = e.target.value;

        onChangeProductKey(next);
        onChangeVersion(undefined);
        setLastVersion(undefined);
        setLastKey('');
        setRows([{ id: uid(), key: '', value: '' }]);
        setAlternates([]);
        setAlternatesAnchor(null);
    };

    const handleProductVersionChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const raw = e.target.value.trim();

        if (raw === '') {
            onChangeVersion(undefined);
            return;
        }
        const parsed = Number(raw);

        if (Number.isFinite(parsed)) {
            onChangeVersion(parsed);
            setLastVersion(undefined);
        }
    };

    return (
        <Card>
            <Typography gutterBottom fontWeight="medium" variant="subtitle1">
                Target Product
            </Typography>
            <StyledHeaderGrid>
                <StyledTextField
                    error={productKey.length > 0 && !prdIsValid && !looksLikeMpvId && !isResolvingMpv}
                    helperText={((): string => {
                        if (mpvResolveError) return mpvResolveError;
                        if (isResolvingMpv) return `Resolving MPV "${prdCandidate}"…`;
                        if (productKey.length > 0 && !prdIsValid && !looksLikeMpvId) return 'Invalid Product Key.';
                        return '';
                    })()}
                    InputProps={{
                        endAdornment: (
                            <StyledInputAdornment position="end">
                                {(prdIsValid && isLoading) || isResolvingMpv
                                    ? <CircularProgress color="inherit" size={18} /> : null}
                            </StyledInputAdornment>
                        ),
                    }}
                    label="Product Key or MPV ID"
                    placeholder="PRD-1234567 or posters"
                    size="small"
                    value={productKey}
                    onChange={handleProductKeyChange}
                />
                <TextField
                    label="Product Version"
                    size="small"
                    value={version ?? ''}
                    onChange={handleProductVersionChange}
                />
                <FormControl fullWidth size="small">
                    <InputLabel id="culture-select-label">Culture</InputLabel>
                    <Select
                        displayEmpty
                        id="culture-select"
                        label="Culture"
                        labelId="culture-select-label"
                        renderValue={(val): string => (val as string) || ''}
                        value={culture}
                        onChange={(e): void => onChangeCulture(e.target.value as string)}
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {Object.entries(filteredGroupedLocales).flatMap(([region, locales]) => ([
                            <ListSubheader key={region}>{region}</ListSubheader>,
                            ...locales.map((loc) => (
                                <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                            )),
                        ]))}
                    </Select>
                </FormControl>
            </StyledHeaderGrid>
            {prdIsValid && (
                <Box
                    sx={{
                        my: 2, display: 'flex', alignItems: 'center', gap: 1,
                    }}
                >
                    {isLoadingProductName && (
                        <Skeleton animation="wave" height={24} width={200} />
                    )}
                    {!isLoadingProductName && productsData?.result?.[0]?.matching_versions?.[0]?.productName && (
                        <>
                            <Typography color="text.secondary" variant="body2">
                                {productsData.result[0].matching_versions[0].productName}
                            </Typography>
                            <Tooltip title="View in PIM">
                                <IconButton
                                    sx={{
                                        padding: 0.5,
                                        color: 'text.secondary',
                                        width: 16,
                                        height: 16,
                                        '& .MuiSvgIcon-fontSizeSmall': {
                                            fontSize: '0.875rem',
                                        },
                                    }}
                                    onClick={(): void => window.open(
                                        `https://pim.products.cimpress.io/merchant/products/${prdCandidate}/versions/${version ?? activeVersion}/overview`,
                                        '_blank',
                                    )?.focus()}
                                >
                                    <OpenInNew sx={{ fontSize: '16px', color: 'text.secondary' }} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Box>
            )}
            {prdIsValid && collapsedAlternates.length > 0 && (
                <Box sx={{
                    mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1,
                }}
                >
                    <Typography color="text.secondary" variant="caption">
                        This MPV also maps to:
                    </Typography>
                    {collapsedAlternates.map((alt) => (
                        <Tooltip key={`${alt.culture}|${alt.productKey}`} title={`Switch to ${alt.productKey} (${alt.culture})`}>
                            <Chip
                                clickable
                                icon={<SwapHoriz sx={{ fontSize: 14 }} />}
                                label={`${alt.productKey} · ${alt.culture}`}
                                size="small"
                                sx={{ height: 22, fontSize: '0.7rem' }}
                                variant="outlined"
                                onClick={(): void => applyAlternate(alt)}
                            />
                        </Tooltip>
                    ))}
                </Box>
            )}
            <Typography fontWeight="medium" sx={{ mb: 1 }} variant="body2">
                Product Options
            </Typography>
            {rows.map((r, idx) => {
                const otherUsed = new Set(
                    rows
                        .filter((_, i) => i !== idx)
                        .map((x) => x.key.trim().toLowerCase())
                        .filter(Boolean),
                );

                const nameOptions: string[] = optionNames.filter(
                    (n) => !otherUsed.has(n.trim().toLowerCase()),
                );
                const valueOptions: string[] = valuesByName[r.key] ?? [];

                return (
                    <RowGrid key={r.id}>
                        <Autocomplete
                            freeSolo
                            disabled={!prdIsValid || !activeVersion}
                            options={nameOptions}
                            renderInput={(params): JSX.Element => (
                                <TextField {...params} label="Option Name" placeholder="e.g., Size" size="small" />
                            )}
                            value={r.key}
                            onInputChange={(_, val): void => updateRowKey(r.id, val)}
                        />
                        <Autocomplete
                            freeSolo
                            disabled={!prdIsValid || !activeVersion}
                            options={valueOptions}
                            renderInput={(params): JSX.Element => (
                                <TextField {...params} label="Option Value" placeholder='e.g., 3"x3"' size="small" />
                            )}
                            value={r.value}
                            onInputChange={(_, val): void => updateRowValue(r.id, val)}
                        />
                        <IconButton
                            aria-label="remove row"
                            disabled={!prdIsValid || !activeVersion || (rows.length <= 1 && idx === rows.length - 1)}
                            size="small"
                            onClick={(): void => removeRow(r.id)}
                        >
                            <Remove />
                        </IconButton>
                    </RowGrid>
                );
            })}
            <div ref={bottomRef} />
        </Card>
    );
};
